'use client';

import { useForm } from 'react-hook-form';

import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import {
  CUSTOMER_PROFILE_MAX_AGE,
  CUSTOMER_PROFILE_MIN_AGE,
} from '@module/mdrt-org/shared/utils/constants/customer-profile';
import {
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  MAX_MIDDLE_NAME_LENGTH,
  MAX_PREFERRED_NAME_LENGTH,
  MAX_YEAR_LENGTH,
} from '@module/mdrt-org/shared/utils/constants/inputs-constants';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { allowOnlyNumbersTransform } from '@module/mdrt-org/shared/utils/helpers/allow-only-numbers';
import { transformYearOfBirthDefaultValue } from '@module/mdrt-org/shared/utils/helpers/transform-default-values';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { LATIN_ONLY_NAME_REGEXP } from '@module/mdrt-org/shared/utils/types/regexp';
import { Button } from '@shared/ui-components/button/button';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import {
  GTM_ACTION_CLICK,
  GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
  GTM_EVENT_USER_INTERACTIONS,
} from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';

import { useAccountSettingsPageContext } from '../../providers/account-settings-page-context';
import { type PersonalInfoData, type PersonalInfoFormData } from '../../utils/types/form-data';
import { type AccountSettingsPersonalInfoContent } from './get-account-settings-personal-info-content';

import styles from './personal-info-form.module.scss';

type PersonalInfoFormProps = {
  cmsContent: AccountSettingsPersonalInfoContent;
  gendersOptions: SelectOption[];
  prefixesOptions: SelectOption[];
  suffixesOptions: SelectOption[];
  tabUrl: string;
};

export const PersonalInfoForm = ({
  cmsContent,
  gendersOptions,
  prefixesOptions,
  suffixesOptions,
  tabUrl,
}: PersonalInfoFormProps) => {
  const {
    customerProfileContextData,
    resetCustomerProfileContextData,
    setCustomerProfileContextData,
  } = useAccountSettingsPageContext();
  const { personId } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();

  const currentYear = new Date().getFullYear();
  const minYearOfBirth = currentYear - CUSTOMER_PROFILE_MAX_AGE;
  const maxYearOfBirth = currentYear - CUSTOMER_PROFILE_MIN_AGE;

  const formMethods = useForm<PersonalInfoFormData>({
    ...formBaseConfig,
    defaultValues: {
      firstName: customerProfileContextData.firstName ?? '',
      gender:
        customerProfileContextData?.gender || customerProfileContextData?.gender === 0
          ? gendersOptions.find(
              (gender) => gender?.value === `${customerProfileContextData.gender}`
            )
          : null,
      lastName: customerProfileContextData.lastName ?? '',
      middleName: customerProfileContextData.middleName ?? '',
      preferredName: customerProfileContextData.nickname ?? '',
      prefix: customerProfileContextData.prefix
        ? {
            label: customerProfileContextData.prefix,
            value: customerProfileContextData.prefix,
          }
        : null,
      suffix: customerProfileContextData.suffix
        ? {
            label: customerProfileContextData.suffix,
            value: customerProfileContextData.suffix,
          }
        : null,
      yearOfBirth: transformYearOfBirthDefaultValue(customerProfileContextData?.birthday),
    },
  });

  const {
    formState: { isDirty },
    reset,
  } = formMethods;

  const onFailure = (result: ServerActionResponse) => {
    if (result.status !== 200 && 'message' in result) {
      resetCustomerProfileContextData();
      addErrorToast();
    }
  };

  const { isMutationPending, mutate } = useMutationHandler({
    mutationFunction: patchAccountSettingsAction<PersonalInfoData>,
    mutationKey: [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS],
    onFailure,
    onSuccessHandler: () => reset({ ...formMethods.getValues() }),
  });

  const handleSubmit = async (data: PersonalInfoFormData) => {
    const formData = {
      firstName: data.firstName,
      gender: Number(data.gender?.value),
      lastName: data.lastName,
      middleName: data.middleName,
      preferredName: data.preferredName,
      prefix: data.prefix && data.prefix.value.toString(),
      suffix: data.suffix && data.suffix.value.toString(),
      yearOfBirth: data.yearOfBirth,
    };

    setCustomerProfileContextData({
      ...customerProfileContextData,
      ...formData,
      birthday: data.yearOfBirth.toString(),
    });

    mutate({
      formData: {
        accountSettingStep: AccountSettingsFormStep.PersonalInformation,
        personalData: formData,
      },
      personId,
    });

    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
      label: 'Personal Information',
    });
  };

  useConfirmNavigation(isDirty, tabUrl);

  return (
    <FormWrapper
      className={styles['personal-info-form']}
      dataTestId="personal-info-form"
      formMethods={formMethods}
      onSubmit={handleSubmit}
    >
      <h1 className={styles['personal-info-form__heading']}>{cmsContent.tabTitle}</h1>
      <SelectField isClearable label={cmsContent.prefix} name="prefix" options={prefixesOptions} />
      <FieldController<PersonalInfoFormData>
        label={cmsContent.firstName}
        name="firstName"
        rules={{
          pattern: {
            message: `${cmsContent.latinOnlyMessage}`,
            value: LATIN_ONLY_NAME_REGEXP,
          },
        }}
      >
        <FormInput maxLength={MAX_FIRST_NAME_LENGTH} />
      </FieldController>
      <FieldController<PersonalInfoFormData>
        label={cmsContent.middleName}
        name="middleName"
        rules={{
          pattern: {
            message: `${cmsContent.latinOnlyMessage}`,
            value: LATIN_ONLY_NAME_REGEXP,
          },
        }}
      >
        <FormInput maxLength={MAX_MIDDLE_NAME_LENGTH} />
      </FieldController>
      <FieldController<PersonalInfoFormData>
        label={cmsContent.lastName}
        name="lastName"
        rules={{
          pattern: {
            message: `${cmsContent.latinOnlyMessage}`,
            value: LATIN_ONLY_NAME_REGEXP,
          },
          required: `${cmsContent.required}`,
        }}
      >
        <FormInput maxLength={MAX_LAST_NAME_LENGTH} />
      </FieldController>
      <SelectField isClearable label={cmsContent.suffix} name="suffix" options={suffixesOptions} />
      <FieldController<PersonalInfoFormData>
        label={cmsContent.preferredName}
        name="preferredName"
        rules={{
          maxLength: MAX_PREFERRED_NAME_LENGTH,
          pattern: {
            message: `${cmsContent.latinOnlyMessage}`,
            value: LATIN_ONLY_NAME_REGEXP,
          },
        }}
      >
        <FormInput maxLength={MAX_PREFERRED_NAME_LENGTH} />
      </FieldController>
      <FieldController<PersonalInfoFormData>
        label={cmsContent.yearOfBirth}
        maxLength={MAX_YEAR_LENGTH}
        name="yearOfBirth"
        rules={{
          max: {
            message: `${cmsContent.yearOfBirthValidationMessage}`,
            value: maxYearOfBirth,
          },
          min: {
            message: `${cmsContent.yearOfBirthValidationMessage}`,
            value: minYearOfBirth,
          },
          required: cmsContent.required,
        }}
        transform={allowOnlyNumbersTransform}
      >
        <FormInput />
      </FieldController>
      <SelectField
        label={cmsContent.gender}
        name="gender"
        options={gendersOptions}
        rules={{
          required: cmsContent.required,
        }}
      />
      <div className={styles['personal-info-form__footer']}>
        <Button disabled={!isDirty || isMutationPending} type={ButtonType.SUBMIT}>
          {cmsContent.saveChanges}
        </Button>
      </div>
    </FormWrapper>
  );
};
